import { openDB } from 'idb';

/**
 * Stockage local - document 02, section 5.
 *
 * Quatre magasins : profiles, quotes, movements, settings. La donnée est
 * écrite d'abord ici
 * et l'application fonctionne intégralement hors-ligne (AD-1) : ce store est la
 * source de vérité opérationnelle, le serveur n'en est qu'un miroir.
 *
 * `idb` plutôt qu'IndexedDB brut : la version native impose un enrobage
 * événementiel d'une centaine de lignes, propice aux fuites de transaction. La
 * bibliothèque pèse moins de deux kilo-octets, ce qui reste compatible avec
 * l'exigence de sobriété.
 */
const NOM = 'corridorfx';
// Version 2 : ajout du magasin `movements` (document 02, section 7). La
// montée de version est le seul moyen de créer un magasin dans IndexedDB, et
// `upgrade` est rejoué depuis l'ancienne version chez qui a déjà des données :
// chaque création reste donc gardée par un test d'existence.
const VERSION = 2;

let promesse = null;

export function ouvrir() {
  if (!promesse) {
    promesse = openDB(NOM, VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('profiles')) {
          const s = db.createObjectStore('profiles', { keyPath: 'id' });
          s.createIndex('order', 'order');
          s.createIndex('deleted', 'deleted');
        }
        if (!db.objectStoreNames.contains('quotes')) {
          const s = db.createObjectStore('quotes', { keyPath: 'id' });
          s.createIndex('createdAt', 'createdAt');
          s.createIndex('status', 'status');
          s.createIndex('deleted', 'deleted');
        }
        if (!db.objectStoreNames.contains('movements')) {
          const s = db.createObjectStore('movements', { keyPath: 'id' });
          // `date` d'abord : c'est l'ordre de rejeu du livre, et le CMP dépend
          // de l'histoire. Un rejeu dans le désordre donnerait des marges
          // fausses sans rien signaler.
          s.createIndex('date', 'date');
          s.createIndex('type', 'type');
          s.createIndex('corridor', 'corridor');
          s.createIndex('deleted', 'deleted');
        }
        // Singleton : une seule entrée, clé fixe. Les réglages sont propres à
        // l'appareil et ne se synchronisent jamais.
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }
      },
    });
  }
  return promesse;
}

/** Champs communs à toute entité synchronisable - document 02, section 1. */
export function neuf(champs) {
  const maintenant = new Date().toISOString();
  return { id: crypto.randomUUID(), createdAt: maintenant, updatedAt: maintenant, deleted: false, ...champs };
}

/**
 * Marque une entité modifiée. `updatedAt` est l'arbitre de la synchro (AD-7) :
 * l'oublier fait perdre la modification au prochain rapprochement.
 */
export const touche = (entite) => ({ ...entite, updatedAt: new Date().toISOString() });

export async function lireTout(magasin) {
  const db = await ouvrir();
  const tout = await db.getAll(magasin);
  // Les entités supprimées restent en base pour propager la suppression, mais
  // ne remontent jamais à l'interface.
  return tout.filter((e) => !e.deleted);
}

export async function ecrire(magasin, entite) {
  const db = await ouvrir();
  await db.put(magasin, entite);
  return entite;
}

/**
 * Suppression logique. On ne supprime jamais physiquement avant synchro : une
 * entité effacée en dur ne propage pas sa disparition aux autres appareils.
 */
export async function supprimer(magasin, id) {
  const db = await ouvrir();
  const entite = await db.get(magasin, id);
  if (!entite) return null;
  const efface = touche({ ...entite, deleted: true });
  await db.put(magasin, efface);
  return efface;
}
